import React, { useEffect, useMemo, useRef, useState } from 'react';
import pb from '../lib/pocketbase';
import { useUsersData } from '../context/UsersContext';
import { useAuth } from '../auth/AuthContext';

function SortIcon({ dir }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginLeft: 6, verticalAlign: 'middle' }}
      aria-hidden="true"
    >
      {dir === 'asc' ? (
        <polyline points="18 15 12 9 6 15" />
      ) : (
        <polyline points="6 9 12 15 18 9" />
      )}
    </svg>
  );
}

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const { users: contextUsers, totalPages: contextTotalPages, loading: contextLoading, refresh } = useUsersData();

  // Initialize with context data to allow instant render
  const [items, setItems] = useState(contextUsers || []);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(contextTotalPages || 1);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const latestReq = useRef(0);
  const [sortBy, setSortBy] = useState('created'); // 'created' | 'name' | 'email'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [savingId, setSavingId] = useState('');

  // Debounce search query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const filter = useMemo(() => {
    if (!debouncedQ.trim()) return '';
    const s = debouncedQ.trim();
    // Search in name or email
    return `name~"${s}" || email~"${s}"`;
  }, [debouncedQ]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const reqId = ++latestReq.current;
      const sortPrefix = sortDir === 'desc' ? '-' : '';
      const sortField = sortBy === 'created' ? 'created' : sortBy; // pocketbase fields
      const res = await pb.collection('users').getList(page, perPage, {
        sort: `${sortPrefix}${sortField}`,
        filter: filter || undefined,
      });
      if (reqId !== latestReq.current) return; // outdated response
      setItems(res.items || []);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      const msg = String(err?.message || '');
      // Ignore SDK auto-cancellation noise, surface other errors
      if (/auto-?cancel/i.test(msg)) return;
      setError(msg || 'Impossible de charger les utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If we are on default view and context has data, use it instantly without fetching
    const isDefaultView = page === 1 && !filter && sortBy === 'created' && sortDir === 'desc';

    if (isDefaultView && contextUsers.length > 0) {
      setItems(contextUsers);
      setTotalPages(contextTotalPages);
      // Ensure loading is false
      setLoading(false);
    } else {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filter, sortBy, sortDir, contextUsers]); // Added contextUsers to keep in sync


  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const updateRole = async (u, role) => {
    if (!u || !role || u.role === role) return;
    // Prevent changing own role from the UI for safety
    if (currentUser?.id === u.id) {
      setError("Vous ne pouvez pas modifier votre propre rôle depuis cette interface.");
      return;
    }
    const displayName = u.name || u.email || u.id;
    const ok = window.confirm(`Confirmer le changement de rôle pour ${displayName} :\n${u.role || 'user'} → ${role} ?`);
    if (!ok) return;
    try {
      setSavingId(u.id);
      await pb.collection('users').update(u.id, { role });
      // Optimistic refresh
      setItems((prev) => prev.map((it) => (it.id === u.id ? { ...it, role } : it)));
    } catch (err) {
      const msg = err?.message || 'Échec de la mise à jour du rôle';
      setError(msg);
    } finally {
      setSavingId('');
    }
  };

  const avatarUrl = (u) => (u?.avatar ? pb.files.getUrl(u, u.avatar, { thumb: '48x48' }) : null);

  return (
    <section className="section">
      <div className="section-inner">
        <div className="section-header">
          <h2>Utilisateurs</h2>
          <div className="admin-users-controls">
            <input
              type="search"
              placeholder="Recherche nom ou email…"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
            />
            <select value={perPage} onChange={(e) => { setPage(1); setPerPage(Number(e.target.value)); }}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
        </div>

        <div className="card">
          {error ? (
            <div className="alert error">{error}</div>
          ) : null}
          {loading ? (
            <div className="admin-table-loading">Chargement…</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>
                      <button type="button" className={`admin-th-btn ${sortBy === 'name' ? 'active' : ''}`} onClick={() => toggleSort('name')}>
                        Nom
                        {sortBy === 'name' && <SortIcon dir={sortDir} />}
                      </button>
                    </th>
                    <th>
                      <button type="button" className={`admin-th-btn ${sortBy === 'email' ? 'active' : ''}`} onClick={() => toggleSort('email')}>
                        Email
                        {sortBy === 'email' && <SortIcon dir={sortDir} />}
                      </button>
                    </th>
                    <th>Rôle</th>
                    <th>
                      <button type="button" className={`admin-th-btn ${sortBy === 'created' ? 'active' : ''}`} onClick={() => toggleSort('created')}>
                        Inscription
                        {sortBy === 'created' && <SortIcon dir={sortDir} />}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                        Aucun utilisateur trouvé
                      </td>
                    </tr>
                  ) : (
                    items.map((u) => (
                      <tr key={u.id}>
                        <td data-label="Utilisateur">
                          <div className="admin-user-cell">
                            {avatarUrl(u) ? (
                              <img src={avatarUrl(u)} alt="Avatar" className="admin-user-avatar" />
                            ) : (
                              <div className="admin-user-avatar placeholder">{(u.name?.[0] || u.email?.[0] || 'U').toUpperCase()}</div>
                            )}
                            <div className="admin-user-meta">
                              <div className="admin-user-name">{u.name || '(sans nom)'}</div>
                              <div className="admin-user-id">{u.id}</div>
                            </div>
                          </div>
                        </td>
                        <td data-label="Email">{u.email}</td>
                        <td data-label="Rôle">
                          <select
                            className="admin-role-select"
                            value={u.role || 'user'}
                            disabled={savingId === u.id}
                            onChange={(e) => updateRole(u, e.target.value)}
                          >
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                          </select>
                        </td>
                        <td data-label="Inscription">{new Date(u.created).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="admin-table-footer">
            <button type="button" className="btn" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>Précédent</button>
            <span className="admin-page-info">Page {page} / {totalPages}</span>
            <button type="button" className="btn" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Suivant</button>
          </div>
        </div>
      </div>
    </section>
  );
}
